/**
 * THE COUNT OVER A PLACE PAGE'S HOMES (SITE-193, Matt 2026-09-24 via the
 * orchestrator: "the map says 700 for sale while the homes block says 705 on
 * /cities/bend ... make both read the same definition from one source").
 *
 * The homes under a place map hold every listing a visitor may see as on the
 * market: Active, and Active Under Contract (an accepted offer, still
 * showing). The map above them draws Active as for sale and puts Active Under
 * Contract with the pending marks. Both now read publicCountState
 * (lib/listing-status-public), so "for sale" is Active on the map, in the
 * homes block's count, and in every buyer group's count; a listing under
 * contract is counted as under contract, never as for sale.
 *
 * Client-safe: PlaceSubdivisionHomes (a client component) and the server-side
 * section builders call it alike.
 */
import { formatCount } from '@/lib/format/count'
import { placeStockListingCount, withCountBasis, type ListingCountBasis } from '@/lib/listing-counts'

export type PlaceCountRow = { standardStatus?: string | null }

export type PlaceHomesCount = { forSale: number; underContract: number }

/** How many of the rows are for sale and how many are under contract (lib/listing-counts). */
export function placeHomesCount(rows: readonly PlaceCountRow[]): PlaceHomesCount {
  const { count, underContract } = placeStockListingCount(rows)
  return { forSale: count, underContract }
}

/**
 * "700 for sale", "533 for sale · 5 under contract, still showing", "2 under
 * contract, still showing", or
 * null for no rows. Never "0 for sale": a group with no listing for sale says
 * only what it holds.
 */
export function placeHomesCountLabel(
  rows: readonly PlaceCountRow[],
  scope?: string | null,
  /**
   * W1 (Muse audit 2026-10-20): the basis the for-sale figure names in plain
   * words ("280 for sale on the map, all home types"), from
   * placeStockListingCount. Omitted, the label reads as it did.
   */
  basis?: ListingCountBasis | null,
): string | null {
  const { forSale, underContract } = placeHomesCount(rows)
  const parts: string[] = []
  // `scope` names the population when the page prints other counts of the same
  // place (Matt 2026-10-04: "699 for sale on the map" beside "726 houses with a
  // Bend address" read as a contradiction until each said what it counts).
  if (forSale > 0) {
    const figure = `${formatCount(forSale)} for sale${scope?.trim() ? ` ${scope.trim()}` : ''}`
    parts.push(basis ? withCountBasis(figure, basis) : figure)
  }
  // The homes hold only the ones still showing; the map over them counts every
  // home under contract, so this says which part it is (2026-09-29).
  if (underContract > 0) parts.push(`${formatCount(underContract)} under contract, still showing`)
  return parts.length > 0 ? parts.join(' · ') : null
}
